/**
 * LocationField: the At a glance line that says where an intake card's
 * client is, directly under Unit.
 *
 * It reads the card's own entry in the prospects list the Team map reads
 * (useMapProspects, the same query key, so one read serves both), and
 * only for an intake card with a client row and a viewer who holds
 * read_clinical_record. The line shows:
 *   - the current location's town and state (locationWords), whenever
 *     the client has a current location row, placed or not; "Town not on
 *     file" when that row names neither;
 *   - the reason the client is not on the map, when not placeable: under
 *     the words, or the whole value when there is no current row;
 *   - "Show on map", only when the entry is placeable with a finite point
 *     and the viewer holds view_workspace: a link to the Team map that
 *     opens Place a client on this card.
 * No line while the list loads, and none when the loaded list does not
 * hold this card (closed, archived, before Client Setup, or off the map
 * by lifecycle). A failed read says "Could not load". The words and the
 * reason wrap and are never cut.
 */

import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { canReadClinicalRecord, canViewWorkspace } from '@/lib/capabilities';
import { useMapProspects } from '@/lib/capacityApi';
import {
  LOCATION_LOAD_FAILED,
  LOCATION_TOWN_MISSING,
  SHOW_ON_MAP_LABEL,
  clientNotPlacedLabel,
  locationWords,
  showOnMapHref,
} from '@/lib/clientLocation';
import { readWorkingStates } from '@/lib/tenantConfig';
import { DrawerRow } from './drawerLayout';

export interface LocationFieldProps {
  workItemId: string;
  kind: string;
  clientId: string | null;
}

/** The reasons that mean the client has a current location row. */
const HAS_CURRENT_ROW = new Set<string>(['not_checked', 'not_found', 'not_found_add_state']);

export function LocationField({ workItemId, kind, clientId }: LocationFieldProps): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const enabled = kind === 'intake' && clientId !== null && canReadClinicalRecord(whoami);
  const { prospects, isError } = useMapProspects(tenantSlug, { enabled });
  const tenantConfig = useTenantConfig(tenantSlug);
  const workingStates = useMemo(() => readWorkingStates(tenantConfig.data), [tenantConfig.data]);

  if (!enabled) return null;
  const entry = prospects.find((p) => p.work_item_id === workItemId);

  if (entry === undefined) {
    if (!isError) return null;
    return (
      <DrawerRow label="Location" testId="drawer-location-row">
        {LOCATION_LOAD_FAILED}
      </DrawerRow>
    );
  }

  const reason = entry.placeable ? null : (entry.not_placeable_reason ?? 'no_address');
  const showWords = reason === null || HAS_CURRENT_ROW.has(reason);
  const words = locationWords(entry.location_city, entry.location_state) || LOCATION_TOWN_MISSING;
  const showLink =
    entry.placeable &&
    entry.lat !== null &&
    entry.lng !== null &&
    Number.isFinite(entry.lat) &&
    Number.isFinite(entry.lng) &&
    canViewWorkspace(whoami);

  return (
    <DrawerRow label="Location" testId="drawer-location-row">
      {showWords && (
        <span className="flex flex-wrap items-baseline gap-x-3">
          <span className="min-w-0" data-testid="drawer-location-words">{words}</span>
          {showLink && (
            <Link
              to={showOnMapHref(tenantSlug, workItemId)}
              data-testid="drawer-show-on-map"
              className="text-sage-800 underline decoration-dotted underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded"
            >
              {SHOW_ON_MAP_LABEL}
            </Link>
          )}
        </span>
      )}
      {reason !== null && (
        <span className="block text-ink-soft" data-testid="drawer-location-reason">
          {clientNotPlacedLabel(reason, workingStates)}
        </span>
      )}
    </DrawerRow>
  );
}
